import { useState } from "react";
import { Mail, Send, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { LoadingButton } from "@/components/loading-button";
import { formatApiErrorMessage } from "@/lib/api-error";
import { DismissActionButton } from "./dismiss-action-button";
import type { useResolveAction, useSendCustomerNotification } from "./hooks";
import { ComposeTextarea } from "@/components/compose-field";
import { EmailRecipientsField } from "@/components/email-recipients-field";
import { useEmailRecipients } from "@/components/use-email-recipients";

/** The delay email Sales sends: the standard wording, then their optional note. */
function delayNoticeBody(template: string, note: string): string {
  const extra = note.trim();
  return extra ? `${template.trim()}\n\n${extra}` : template.trim();
}

/**
 * "Delayed: notify the customer?" (USA-1426). Sales either opens the standard delay email,
 * adds an optional note and sends it, or dismisses the item so the customer hears nothing.
 */
export function DelayNoticeChoice({
  actionId,
  subject,
  template,
  customerName,
  customerEmail,
  sendNotification,
  resolveAction,
  disabled,
}: {
  actionId: number;
  subject: string;
  template: string;
  customerName?: string | null;
  customerEmail?: string | null;
  sendNotification: ReturnType<typeof useSendCustomerNotification>;
  resolveAction: ReturnType<typeof useResolveAction>;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState("");
  const recipients = useEmailRecipients({ to: customerEmail ? [customerEmail] : [], cc: [] });

  if (!open) {
    return (
      <div className="flex flex-wrap gap-2 border-t pt-3">
        <Button size="sm" disabled={disabled} onClick={() => setOpen(true)}>
          <Mail className="size-3.5" aria-hidden="true" /> Notify customer
        </Button>
        <DismissActionButton
          actionId={actionId}
          resolveAction={resolveAction}
          variant="outline"
          disabled={disabled}
        >
          <X className="size-3.5" aria-hidden="true" /> Don&apos;t notify
        </DismissActionButton>
      </div>
    );
  }

  const send = () =>
    sendNotification.mutate(
      { actionId, subject, bodyText: delayNoticeBody(template, note), recipients: recipients.override },
      {
        onSuccess: () => toast.success("Delay email sent to the customer"),
        onError: (err) =>
          toast.error(formatApiErrorMessage("Email not sent", err), {
            description: "Your note is still here. Try again.",
          }),
      },
    );

  return (
    <div className="space-y-3 border-t pt-3">
      <EmailRecipientsField
        state={recipients}
        toName={customerName}
        fallback="The order's customer"
        disabled={sendNotification.isPending}
      />
      <div className="space-y-2 text-sm whitespace-pre-line">{template}</div>
      <div className="space-y-1">
        <label
          htmlFor={`delay-note-${actionId}`}
          className="text-xs text-muted-foreground"
        >
          Add a note (optional)
        </label>
        <ComposeTextarea
          id={`delay-note-${actionId}`}
          value={note}
          onChange={(event) => setNote(event.target.value)}
          rows={3}
          placeholder="Shown after the delay message, e.g. an option we can offer."
        />
      </div>
      <div className="flex flex-wrap gap-2">
        <LoadingButton
          size="sm"
          loading={sendNotification.isPending}
          loadingText="Sending..."
          disabled={disabled}
          onClick={send}
        >
          <Send className="size-3.5" aria-hidden="true" /> Send to customer
        </LoadingButton>
        <Button
          size="sm"
          variant="ghost"
          disabled={sendNotification.isPending}
          onClick={() => setOpen(false)}
        >
          Cancel
        </Button>
      </div>
    </div>
  );
}
